import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";

import { getBotEmbedMock, normalizeBotEmbedConfig } from "./mockData";
import { handleGenericFileUpload } from "../../utils/common";
 


const getIpInfo = async () => {
  if (process.env.NODE_ENV === "development") {
    return { countryName: "India" };
  }

  try {
    // This key is publicly exposed in the source code.
    
    const url = `${process.env.REACT_APP_CHAT_BASE_URL}visitor-location`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Failed to fetch IP-based location.", error);
    return { countryName: "India" };
  }
};

const getCountry = async () => {
  if (typeof window === "undefined") {
    return "India";
  }

  const storedCountry = window.localStorage.getItem("country_name");
  if (storedCountry) {
    return storedCountry;
  }

  try {
    const ipData = await getIpInfo();
    const countryName = ipData?.countryName;

    if (countryName) {
      window.localStorage.setItem("country", countryName);
      return countryName;
    }
  } catch (error) {
    // Fallback is handled below
  }

  return "India";
};

const BotEmbedContext = createContext(null);
const getBotEmbedApiUrl = () => {
  const userApiBaseUrl = (process.env.REACT_APP_BOT_EMBED_API_URL || "")
    .trim()
    .replace(/\/+$/, "");

  console.log(userApiBaseUrl, "userApiBaseUrl");
  return userApiBaseUrl;
};

const BOT_EMBED_API_URL = getBotEmbedApiUrl();

const DEFAULT_LANGUAGE_CODE = "en";

const createClientId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `bb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getBrowserUniqueId = () => {
  if (typeof window === "undefined") {
    return createClientId();
  }

  const existingId = window.localStorage.getItem("browser_unique_id");

  if (existingId) {
    return existingId;
  }

  const nextId = createClientId();
  window.localStorage.setItem("browser_unique_id", nextId);
  return nextId;
};

const buildSocketURL = (baseURL, params = {}) => {
  const url = new URL(baseURL);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
};

const usesTestSocketMode = (mode) => mode === "test" || mode === "preview";

const resolveSocketBaseURL = ({
  botId,
  configBotId,
  configSettingId,
  mode,
}) => {
  if (
    usesTestSocketMode(mode) &&
    configBotId &&
    process.env.REACT_APP_TEST_SOCKET_URL
  ) {
    return `${process.env.REACT_APP_TEST_SOCKET_URL}${configBotId}/`;
  }

  if (configSettingId && process.env.REACT_APP_EXTERNAL_SOCKET_URL) {
    return `${process.env.REACT_APP_EXTERNAL_SOCKET_URL}${configSettingId}/`;
  }

  if (botId && process.env.REACT_APP_EXTERNAL_SOCKET_URL) {
    return `${process.env.REACT_APP_EXTERNAL_SOCKET_URL}${botId}/`;
  }

  return "";
};

const resolveSelectedLanguage = (languageValue = "") => {
  const normalizedValue = String(languageValue || "")
    .trim()
    .toLowerCase();

  if (!normalizedValue) {
    return DEFAULT_LANGUAGE_CODE;
  }

  if (normalizedValue === "english") {
    return "en";
  }

  if (normalizedValue.length === 2) {
    return normalizedValue;
  }

  return DEFAULT_LANGUAGE_CODE;
};

const getUserPayload = (socketData = {}) => {
  const messagePayload = socketData?.message;

  if (!messagePayload || typeof messagePayload !== "object") {
    return null;
  }

  if (messagePayload?.bot || messagePayload?.session_resumed) {
    return null;
  }

  if (
    messagePayload?.user_message ||
    messagePayload?.replay ||
    messagePayload?.masked_replay ||
    socketData?.sender === "user"
  ) {
    return messagePayload;
  }

  return null;
};

const getBotPayload = (socketData = {}) => {
  if (socketData?.message?.bot) {
    return socketData.message.bot;
  }

  if (
    socketData?.message &&
    typeof socketData.message === "object" &&
    !socketData?.sender
  ) {
    return socketData.message;
  }

  return null;
};

const getBotMessageText = (botPayload = {}) =>
  botPayload?.question || botPayload?.message || botPayload?.cc_reply || "";

const getMessageAttachment = (messagePayload = {}) => {
  const attachmentValue =
    messagePayload?.file_attached_data ||
    messagePayload?.file_data ||
    messagePayload?.uploaded_file ||
    messagePayload?.attachment ||
    messagePayload?.attachments;

  if (typeof attachmentValue === "string") {
    return attachmentValue;
  }

  if (Array.isArray(attachmentValue)) {
    const firstAttachment = attachmentValue.find(Boolean);
    if (typeof firstAttachment === "string") {
      return firstAttachment;
    }

    return (
      firstAttachment?.url ||
      firstAttachment?.file_link ||
      firstAttachment?.link ||
      ""
    );
  }

  return (
    attachmentValue?.url ||
    attachmentValue?.file_link ||
    attachmentValue?.link ||
    ""
  );
};

const normalizeActionItems = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `action-${index}-${item}`,
          label: item,
          value: item,
        };
      }

      const label =
        item?.label ||
        item?.text ||
        item?.title ||
        item?.name ||
        item?.value ||
        "";

      if (!label) {
        return null;
      }

      return {
        id: item?.id || item?.value || `action-${index}-${label}`,
        label,
        value: item?.value || label,
        url: item?.url || item?.href || item?.link || "",
      };
    })
    .filter(Boolean);
};

const normalizeLinkItems = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `link-${index}-${item}`,
          label: item,
          url: item,
        };
      }

      const url = item?.url || item?.href || item?.link || "";
      const label = item?.label || item?.text || item?.title || url;

      if (!url || !label) {
        return null;
      }

      return {
        id: item?.id || `link-${index}-${label}`,
        label,
        url,
      };
    })
    .filter(Boolean);
};

const getUserMessageText = (userPayload = {}) =>
  userPayload?.user_message ||
  userPayload?.replay ||
  userPayload?.masked_replay ||
  userPayload?.message ||
  "";

const buildMessageId = ({
  role = "",
  uniqueId = "",
  datetime = "",
  fallbackId = "",
}) => {
  const idParts = [role, uniqueId || fallbackId || "", datetime || ""].filter(
    Boolean,
  );

  if (idParts.length > 0) {
    return idParts.join("|");
  }

  return `${role || "message"}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const buildServerMessageKey = ({
  role = "",
  uniqueId = "",
  datetime = "",
  text = "",
}) => `${role}|${uniqueId || ""}|${datetime || ""}|${text || ""}`;

const getLatestAssistantPrompt = (messages = []) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.role !== "assistant" || message?.isStreaming) {
      continue;
    }

    return message;
  }

  return null;
};

const findMessageIndexById = (messages = [], messageId = "") =>
  messages.findIndex((message) => message.id === messageId);

const findMessageIndexByServerKey = (messages = [], serverMessageKey = "") => {
  if (!serverMessageKey) {
    return -1;
  }

  return messages.findIndex(
    (message) => message.serverMessageKey === serverMessageKey,
  );
};

const findLatestOptimisticUserIndex = (
  messages = [],
  { text = "", attachmentUrl = "" } = {},
) => {
  if (!text && !attachmentUrl) {
    return -1;
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.role !== "user") {
      continue;
    }

    if (message?.serverMessageKey) {
      continue;
    }

    if (message?.text !== text) {
      continue;
    }

    if ((message?.attachmentUrl || "") !== attachmentUrl) {
      continue;
    }

    return index;
  }

  return -1;
};

const findLatestStreamingAssistantIndex = (
  messages = [],
  { sessionId = "" } = {},
) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.role !== "assistant" || !message?.isStreaming) {
      continue;
    }

    if (sessionId && message?.sessionId && message.sessionId !== sessionId) {
      continue;
    }

    return index;
  }

  return -1;
};

const buildAssistantMessage = ({
  id = "",
  text = "",
  sessionId = "",
  botPayload = {},
  isStreaming = false,
  isHtml = false,
  serverMessageKey = "",
  serverDatetime = "",
}) => ({
  id,
  role: "assistant",
  text,
  type: botPayload?.type || "",
  ccStatus: botPayload?.cc_status || "",
  isStreaming,
  isHtml,
  attachmentUrl: getMessageAttachment(botPayload),
  buttons: normalizeActionItems(botPayload?.buttons),
  options: normalizeActionItems(botPayload?.options),
  links: normalizeLinkItems(botPayload?.links),
  raw: botPayload,
  meta: botPayload,
  sessionId,
  serverMessageKey,
  serverDatetime,
});

export const BotEmbedProvider = ({ botId, mode, children }) => {
  const [config, setConfig] = useState(() => getBotEmbedMock(botId));
  const [isOpen, setIsOpen] = useState(
    () => getBotEmbedMock(botId).show_default === 1 || mode !== "embed",
  );
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState(
    () => getBotEmbedMock(botId).transcript,
  );
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [isWaitingForFirstSocketMessage, setIsWaitingForFirstSocketMessage] =
    useState(true);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [composerError, setComposerError] = useState("");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    resolveSelectedLanguage(getBotEmbedMock(botId).language),
  );
  const [socketResetCount, setSocketResetCount] = useState(0);
  const [country, setCountry] = useState(null);
  const configBotId = config.bot_id || "";
  const configSettingId = config.setting_id || "";
  const socketRef = useRef(null);
  const pendingAssistantRef = useRef({
    messageId: "",
    sessionId: "",
  });
  const resetPendingAssistant = () => {
    pendingAssistantRef.current = {
      messageId: "",
      sessionId: "",
    };
  };

  useEffect(() => {
    const fetchCountry = async () => {
      const countryName = await getCountry();
      setCountry(countryName);
    };

    fetchCountry();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fallbackConfig = getBotEmbedMock(botId);

    setConfig(fallbackConfig);
    setMessages(fallbackConfig.transcript);
    setDraft("");
    setIsTyping(false);
    setLoadError("");
    setIsLoadingConfig(true);
    setIsUsingFallback(false);
    setIsWaitingForFirstSocketMessage(true);
    setAttachmentUrl("");
    setComposerError("");
    setIsUploadingAttachment(false);
    setSelectedLanguage(resolveSelectedLanguage(fallbackConfig.language));
    setIsOpen(fallbackConfig.show_default === 1 || mode !== "embed");
    resetPendingAssistant();

    const fetchBotEmbedConfig = async () => {
      try {
        const response = await axios.get(BOT_EMBED_API_URL, {
          headers: {
            domain: typeof window !== "undefined" ? window.location.href : "",
            shortcode: botId || fallbackConfig.setting_id,
          },
          signal: controller.signal,
        });

        const nextRecord = response?.data?.data?.[0];

        if (!nextRecord) {
          throw new Error("No bot embed data returned.");
        }

        const nextConfig = normalizeBotEmbedConfig(nextRecord, botId);

        setConfig(nextConfig);
        setMessages(nextConfig.transcript);
        setSelectedLanguage(resolveSelectedLanguage(nextConfig.language));
        setIsOpen(nextConfig.show_default === 1 || mode !== "embed");
      } catch (error) {
        if (
          axios.isCancel(error) ||
          error?.code === "ERR_CANCELED" ||
          controller.signal.aborted
        ) {
          return;
        }

        setConfig(fallbackConfig);
        setMessages(fallbackConfig.transcript);
        setIsUsingFallback(true);
        setLoadError(
          error?.response?.data?.msg ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to load bot embed settings.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingConfig(false);
        }
      }
    };

    fetchBotEmbedConfig();

    return () => {
      controller.abort();
    };
  }, [botId, mode]);

  useEffect(() => {
    if (typeof window === "undefined" || isLoadingConfig || !country) {
      return undefined;
    }

    let isStaleSocket = false;

    const baseSocketURL = resolveSocketBaseURL({
      botId,
      configBotId,
      configSettingId,
      mode,
    });

    if (!baseSocketURL) {
      setIsWaitingForFirstSocketMessage(false);
      return undefined;
    }

    if (
      socketRef.current &&
      (socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      socketRef.current.close();
    }

    const currentLocation = new URL(window.location.href);
    let socketURL = buildSocketURL(baseSocketURL, {
      domain: currentLocation.origin,
      path: currentLocation.pathname,
      env:
        process.env.REACT_APP_URL_ENVIRONMENT ||
        (process.env.NODE_ENV === "production" ? "production" : "development"),
      browser_unique_id: getBrowserUniqueId(),
      session_id: createClientId(),
      botUserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      bot_id: configBotId || botId,
      country,
    });

    if (usesTestSocketMode(mode) && configBotId) {
      socketURL = buildSocketURL(socketURL, {
        env:
          process.env.REACT_APP_URL_ENVIRONMENT ||
          (process.env.NODE_ENV === "production"
            ? "production"
            : "development"),
      });
    }

    const socket = new WebSocket(socketURL);
    socketRef.current = socket;
    setIsWaitingForFirstSocketMessage(true);

    socket.onmessage = (event) => {
      if (isStaleSocket) {
        return;
      }

      let socketData = null;

      try {
        socketData = JSON.parse(event.data);
      } catch (error) {
        console.error("Bot embed socket message parse error:", error);
        return;
      }

      setIsWaitingForFirstSocketMessage(false);

      if (socketData?.detected_language) {
        setSelectedLanguage(
          resolveSelectedLanguage(socketData.detected_language),
        );
      }

      if (socketData?.message?.session_resumed) {
        resetPendingAssistant();
        pendingAssistantRef.current.sessionId = socketData?.session_id || "";
        setIsTyping(false);
        return;
      }

      const userPayload = getUserPayload(socketData);

      if (userPayload) {
        const userText = getUserMessageText(userPayload);
        const userAttachmentUrl = getMessageAttachment(userPayload);
        const userMessageId = buildMessageId({
          role: "user",
          uniqueId: userPayload?.uniqueId || "",
          fallbackId: userPayload?.id || "",
          datetime: userPayload?.datetime || socketData?.datetime || "",
        });
        const userServerKey = buildServerMessageKey({
          role: "user",
          uniqueId: userPayload?.uniqueId || userPayload?.id || "",
          datetime: userPayload?.datetime || socketData?.datetime || "",
          text: userText,
        });

        if (!userText && !userAttachmentUrl) {
          return;
        }

        setMessages((currentMessages) => {
          if (
            findMessageIndexByServerKey(currentMessages, userServerKey) !== -1
          ) {
            return currentMessages;
          }

          const optimisticUserIndex = findLatestOptimisticUserIndex(
            currentMessages,
            {
              text: userText,
              attachmentUrl: userAttachmentUrl,
            },
          );

          if (optimisticUserIndex !== -1) {
            return currentMessages.map((message, index) =>
              index === optimisticUserIndex
                ? {
                    ...message,
                    id: userMessageId || message.id,
                    text: userText || message.text,
                    attachmentUrl: userAttachmentUrl || message.attachmentUrl,
                    sessionId:
                      socketData?.session_id || message.sessionId || "",
                    serverMessageKey: userServerKey,
                    serverDatetime:
                      userPayload?.datetime || socketData?.datetime || "",
                  }
                : message,
            );
          }

          return [
            ...currentMessages,
            {
              id:
                userMessageId ||
                `user-history-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2, 10)}`,
              role: "user",
              text: userText,
              attachmentUrl: userAttachmentUrl,
              sessionId: socketData?.session_id || "",
              serverMessageKey: userServerKey,
              serverDatetime:
                userPayload?.datetime || socketData?.datetime || "",
            },
          ];
        });
        return;
      }

      if (socketData?.sender && socketData.sender !== "bot") {
        return;
      }

      if (socketData?.type === "stream_chunk") {
        const nextChunk = socketData?.chunk || "";
        const sessionId =
          socketData?.session_id || pendingAssistantRef.current.sessionId || "";

        if (!nextChunk) {
          return;
        }

        // Assign ID and update ref outside setMessages to avoid stale-ref issues
        // when the message.bot handler resets the ref before these callbacks run.
        const streamMessageId =
          pendingAssistantRef.current.messageId ||
          `assistant-stream-${Date.now()}`;
        pendingAssistantRef.current = { messageId: streamMessageId, sessionId };

        setMessages((currentMessages) => {
          const currentStreamIndex = findMessageIndexById(
            currentMessages,
            streamMessageId,
          );

          if (currentStreamIndex === -1) {
            return [
              ...currentMessages,
              {
                id: streamMessageId,
                role: "assistant",
                text: nextChunk,
                isStreaming: true,
                isHtml: false,
                sessionId,
              },
            ];
          }

          return currentMessages.map((message, index) =>
            index === currentStreamIndex
              ? {
                  ...message,
                  text: `${message.text || ""}${nextChunk}`,
                  isStreaming: true,
                  sessionId: sessionId || message.sessionId || "",
                }
              : message,
          );
        });
        return;
      }

      if (socketData?.type === "stream_end") {
        const finalText = socketData?.final_text || "";
        if (!finalText) {
          return;
        }

        const pendingMessageId = pendingAssistantRef.current.messageId;
        const sessionId = socketData?.session_id || "";

        setMessages((currentMessages) => {
          const streamingAssistantIndex = pendingMessageId
            ? currentMessages.findIndex(
                (message) => message.id === pendingMessageId,
              )
            : findLatestStreamingAssistantIndex(currentMessages);

          if (streamingAssistantIndex === -1) {
            return currentMessages;
          }

          return currentMessages.map((message, index) =>
            index === streamingAssistantIndex
              ? {
                  ...message,
                  text: finalText,
                  isHtml: true,
                  isStreaming: false,
                  sessionId: sessionId || message.sessionId || "",
                }
              : message,
          );
        });
        resetPendingAssistant();

        setIsTyping(false);
        return;
      }

      const botPayload = getBotPayload(socketData);

      if (!botPayload) {
        return;
      }

      const finalText = getBotMessageText(botPayload);
      const sessionId =
        socketData?.session_id || pendingAssistantRef.current.sessionId || "";
      const assistantServerKey = buildServerMessageKey({
        role: "assistant",
        uniqueId: botPayload?.uniqueId || botPayload?.id || "",
        datetime: botPayload?.datetime || socketData?.datetime || "",
        text: finalText,
      });
      const assistantMessageId = buildMessageId({
        role: "assistant",
        uniqueId: botPayload?.uniqueId || "",
        fallbackId: botPayload?.id || "",
        datetime: botPayload?.datetime || socketData?.datetime || "",
      });
      const finalMessageId =
        pendingAssistantRef.current.messageId ||
        assistantMessageId ||
        `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const finalMessage = buildAssistantMessage({
        id: finalMessageId,
        text: finalText,
        sessionId,
        botPayload,
        isStreaming: false,
        isHtml: true,
        serverMessageKey: assistantServerKey,
        serverDatetime: botPayload?.datetime || socketData?.datetime || "",
      });

      if (
        !finalMessage.text &&
        !finalMessage.attachmentUrl &&
        finalMessage.buttons.length === 0 &&
        finalMessage.options.length === 0 &&
        finalMessage.links.length === 0
      ) {
        return;
      }

      if (pendingAssistantRef.current.messageId) {
        // Capture the ID now — resetPendingAssistant() runs synchronously below,
        // before the setMessages callback is ever invoked by React.
        const capturedPendingId = pendingAssistantRef.current.messageId;

        setMessages((currentMessages) => {
          const pendingAssistantIndex = findMessageIndexById(
            currentMessages,
            capturedPendingId,
          );
          const streamingAssistantIndex = findLatestStreamingAssistantIndex(
            currentMessages,
            {
              sessionId,
            },
          );
          const assistantIndexToFinalize =
            pendingAssistantIndex !== -1
              ? pendingAssistantIndex
              : streamingAssistantIndex;

          if (assistantIndexToFinalize === -1) {
            return [...currentMessages, finalMessage];
          }

          return currentMessages.map((message, index) =>
            index === assistantIndexToFinalize
              ? {
                  ...message,
                  id: finalMessage.id,
                  text: finalMessage.text,
                  isStreaming: false,
                  isHtml: true,
                  type: finalMessage.type,
                  ccStatus: finalMessage.ccStatus,
                  attachmentUrl: finalMessage.attachmentUrl,
                  buttons: finalMessage.buttons,
                  options: finalMessage.options,
                  links: finalMessage.links,
                  raw: finalMessage.raw,
                  meta: finalMessage.meta,
                  sessionId: finalMessage.sessionId,
                  serverMessageKey: finalMessage.serverMessageKey,
                  serverDatetime: finalMessage.serverDatetime,
                }
              : message,
          );
        });
        resetPendingAssistant();
        setIsTyping(false);
        return;
      }

      setMessages((currentMessages) => {
        const streamingAssistantIndex = findLatestStreamingAssistantIndex(
          currentMessages,
          {
            sessionId,
          },
        );
        const existingAssistantIndex = findMessageIndexByServerKey(
          currentMessages,
          assistantServerKey,
        );

        if (existingAssistantIndex !== -1) {
          return currentMessages.map((message, index) =>
            index === existingAssistantIndex ? finalMessage : message,
          );
        }

        if (streamingAssistantIndex !== -1) {
          return currentMessages.map((message, index) =>
            index === streamingAssistantIndex ? finalMessage : message,
          );
        }

        return [...currentMessages, finalMessage];
      });
      setIsTyping(false);
    };

    socket.onerror = () => {
      if (isStaleSocket) {
        return;
      }

      setIsWaitingForFirstSocketMessage(false);
      resetPendingAssistant();
      setIsTyping(false);
    };

    socket.onclose = () => {
      if (isStaleSocket) {
        return;
      }

      setIsWaitingForFirstSocketMessage(false);
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };

    return () => {
      isStaleSocket = true;

      if (socketRef.current === socket) {
        socketRef.current = null;
      }

      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
    };
  }, [
    botId,
    configBotId,
    configSettingId,
    isLoadingConfig,
    mode,
    socketResetCount,
    country,
  ]);

  const activeAssistantPrompt = getLatestAssistantPrompt(messages);
  const currentInputType = activeAssistantPrompt?.type || "";

  const uploadAttachment = async (file) => {
    if (!file || isTyping || isWaitingForFirstSocketMessage) {
      return;
    }

    setComposerError("");
    setIsUploadingAttachment(true);

    try {
      if (
        typeof navigator !== "undefined" &&
        "onLine" in navigator &&
        !navigator.onLine
      ) {
        throw new Error("No internet connection.");
      }

      const response = await handleGenericFileUpload({
        shortCode: botId || config.setting_id || "",
        file,
      });

      if (!response?.status) {
        throw new Error(response?.msg || "Failed to upload the selected file.");
      }

      setAttachmentUrl(response?.res?.data?.data?.file_link || "");
    } catch (error) {
      setComposerError(error?.message || "Failed to upload the selected file.");
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const sendMessage = (text, options = {}) => {
    const trimmedMessage =
      typeof text === "string" ? text.trim() : String(text || "").trim();
    const selectedOption = options?.option || null;
    const nextQuestionMeta =
      options?.questionMeta || activeAssistantPrompt?.meta || {};
    const nextQuestionText =
      options?.questionText || nextQuestionMeta?.question || "";
    const pendingAttachmentUrl = options?.attachmentUrl ?? attachmentUrl;
    const nextMessageLabel =
      selectedOption?.label || selectedOption?.value || trimmedMessage;
    const nextMessageValue = selectedOption?.value || trimmedMessage;

    if (
      isTyping ||
      isWaitingForFirstSocketMessage ||
      isUploadingAttachment ||
      (!nextMessageValue && !pendingAttachmentUrl)
    ) {
      return;
    }

    setComposerError("");

    const payload = {
      default_language: selectedLanguage || DEFAULT_LANGUAGE_CODE,
    };

    if (nextQuestionText) {
      payload.question = nextQuestionText;
    }

    if (pendingAttachmentUrl) {
      payload.file_data = pendingAttachmentUrl;
    }

    if (selectedOption) {
      payload.id = selectedOption?.value || selectedOption?.id || "";
      payload.user_message = nextMessageLabel;
    } else if (nextMessageValue) {
      payload.id = nextQuestionMeta?.id || "";
      payload.user_message = nextMessageValue;
    }

    const optimisticUserMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: payload.user_message || "",
      attachmentUrl: pendingAttachmentUrl || "",
      serverDatetime: new Date().toISOString(),
    };

    setMessages((currentMessages) => [
      ...currentMessages,
      optimisticUserMessage,
    ]);
    setDraft("");
    setAttachmentUrl("");
    pendingAssistantRef.current = {
      messageId: `assistant-pending-${Date.now()}`,
      sessionId: "",
    };
    setIsTyping(true);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          message: payload,
          path: typeof window !== "undefined" ? window.location.pathname : "/",
          consent: true,
          env:
            process.env.REACT_APP_URL_ENVIRONMENT ||
            (process.env.NODE_ENV === "production"
              ? "production"
              : "development"),
          selected_language: selectedLanguage || DEFAULT_LANGUAGE_CODE,
        }),
      );
      return;
    }

    resetPendingAssistant();
    setIsTyping(false);
    setComposerError(
      "The chat connection is still starting. Please try again in a moment.",
    );
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `assistant-error-${Date.now()}`,
        role: "assistant",
        text: "The chat connection is still starting. Please send your message again in a moment.",
      },
    ]);
  };

  const resetConversation = () => {
    const nextTranscript = Array.isArray(config.transcript)
      ? config.transcript
      : [];
    const activeSocket = socketRef.current;

    setMessages(nextTranscript);
    setDraft("");
    setAttachmentUrl("");
    setComposerError("");
    setIsUploadingAttachment(false);
    setIsTyping(false);
    setIsWaitingForFirstSocketMessage(true);
    setSelectedLanguage(resolveSelectedLanguage(config.language));
    resetPendingAssistant();

    if (typeof window !== "undefined") {
      window.localStorage.setItem("browser_unique_id", createClientId());
    }

    socketRef.current = null;

    if (
      activeSocket &&
      (activeSocket.readyState === WebSocket.OPEN ||
        activeSocket.readyState === WebSocket.CONNECTING)
    ) {
      activeSocket.close();
    }

    setSocketResetCount((currentCount) => currentCount + 1);
  };

  return (
    <BotEmbedContext.Provider
      value={{
        botId,
        config,
        draft,
        isOpen,
        isLoadingConfig,
        isTyping,
        isUsingFallback,
        isWaitingForFirstSocketMessage,
        attachmentUrl,
        composerError,
        currentInputType,
        isUploadingAttachment,
        loadError,
        messages,
        mode,
        setDraft,
        setAttachmentUrl,
        setIsOpen,
        resetConversation,
        sendMessage,
        uploadAttachment,
      }}
    >
      {children}
    </BotEmbedContext.Provider>
  );
};

export const useBotEmbed = () => {
  const context = useContext(BotEmbedContext);

  if (!context) {
    throw new Error("useBotEmbed must be used inside BotEmbedProvider");
  }

  return context;
};
